import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import styled, {css} from "styled-components";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose, faEdit} from "@fortawesome/free-solid-svg-icons";

// Utils
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Components
import Modal from "../../components/Modal.js";
import ModalCustomTableTarget from "../general/ModalCustomTableTarget.js";
import InputText from "../../components/form/InputText.js";
import InputCheck from "../../components/form/InputCheck.js";
import InputSelect from "../../components/form/InputSelect.js";
import InputDate from "../../components/form/InputDate.js";
import InputNumber from "../../components/form/InputNumber.js";
import InputTime from "../../components/form/InputTime.js";
import InputError from "../../components/form/InputError.js";
import {InputRadioGroup} from "../../components/form/FormInputs.js";
import SearchSelect from "../../components/SearchSelect.js";

// Style
import {border, pad, radius} from "../../style/components/variables.js";
import {
  HeadingCenter,
  Button,
  FormField,
  Form,
  FormGroup,
  Label,
  Warning,
  Abbr,
  Inline,
  Text,
  ButtonLoader,
  NotLoaded,
  Loader
} from "../../style/components/general.js";

const ModalEditMeta = ({visible, setVisible, reportMeta, save}) => {
  const isMounted = useMountedState();

  const [targetFolder, setTargetFolder] = useState();
  const [targetFolderHasChildren, setTargetFolderHasChildren] = useState();
  const [editTargetFolder, setEditTargetFolder] = useState(false);
  const [checksheets, setChecksheets] = useState();
  const [events, setEvents] = useState();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(!!reportMeta.completionTaskId);

  const {api: apiMS} = useApi("microsoft");
  const {api: apiChecksheets} = useApi("checksheets");
  const {api: apiEvents} = useApi("events");

  const root = useRef();

  const {api: apiFrequency, data: frequencies} = useApi("frequencies");
  const {api: apiFacilityUser} = useApi("facility-users");

  const {facility} = useContext(FacilityNavContext);

  const schema = yup.object().shape({
    name: yup.string().required("Please provide name"),
    interval: yup
      .number()
      .transform((v, o) => (o === "" ? null : v))
      .nullable()
      .required("Please provide interval"),
    base: yup.string().nullable().required("Please provide base"),
    hasFrequency: yup.bool(),
    frequency: yup.string().when("hasFrequency", {
      is: true,
      then: () => yup.string().required("Frequency is required.")
    }),
    startDate: yup
      .string()
      .nullable()
      .when("hasFrequency", {
        is: true,
        then: () => yup.date().typeError("Start date is required.")
      }),
    startTime: yup
      .string()
      .nullable()
      .when("hasFrequency", {
        is: true,
        then: () => yup.string().time().required("Start time is required.")
      }),
    start: yup
      .string()
      .nullable()
      .when("template", {
        is: false,
        then: () => yup.date().typeError("Start date is required.")
      }),
    end: yup.string().when("template", {
      is: false,
      then: () => yup.date().typeError("End date is required.")
    })
  });

  const form = useForm({resolver: yupResolver(schema)});
  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: {errors}
  } = form;

  const hasHeader = watch("hasHeader");
  const isAutomated = watch("isAutomated");
  const automationType = watch("automationType");
  const completionTaskType = watch("completionTaskType");
  const completionTaskRecipients = watch("completionTaskRecipients");

  useEffect(() => {
    if (checksheets && events) setLoading(false);
  }, [checksheets, events]);

  useEffect(() => {
    if (isMounted()) {
      apiFrequency.callGet();
      const {header, frequency, completionTaskId} = reportMeta;
      reset({
        ...reportMeta,
        hasHeader: header !== null,
        isAutomated: !!(frequency || completionTaskId),
        automationType: completionTaskId ? "completion" : "frequency"
      });
    }
  }, [isMounted, apiFrequency, reportMeta, reset]);

  const getChecksheets = useCallback(
    () =>
      apiChecksheets.callGet(null, {facilityId: facility?.id}).then(({status, data}) => {
        if (status === 200 && data) setChecksheets(data);
      }),
    [apiChecksheets, facility]
  );

  const getEvents = useCallback(
    () =>
      apiEvents.callGet(null, {facilityId: facility?.id}).then(({status, data}) => {
        if (status === 200 && data) setEvents(data);
      }),
    [apiEvents, facility]
  );

  useEffect(() => {
    if (automationType === "completion" && facility) {
      setLoading(true);
      getChecksheets();
      getEvents();
    }
  }, [facility, automationType, getChecksheets, getEvents]);

  const completionOptions = useMemo(() => {
    if (completionTaskType === "checksheet" && checksheets)
      return checksheets.map(({id, name, frequency: taskFreq}) => {
        let labelToUse = name;
        if (taskFreq?.name) labelToUse = `${taskFreq.name.toUpperCase()} ${labelToUse}`;

        return {
          label: labelToUse,
          name: id
        };
      });

    if (completionTaskType === "event" && events)
      return events.map(({id, name, frequency: taskFreq}) => {
        let labelToUse = name;
        if (taskFreq?.name) labelToUse = `${taskFreq.name.toUpperCase()} ${labelToUse}`;

        return {
          label: labelToUse,
          name: id
        };
      });

    return [];
  }, [checksheets, completionTaskType, events]);

  const handleSearchUser = query => {
    const filter = {
      Omit: completionTaskRecipients?.map(user => user),
      showHidden: true,
      State: ["Disabled", "Active"]
    };

    if (query) filter.Search = query;

    const params = {
      facilityId: facility?.id,
      limit: query ? 5 : 20,
      filter: JSON.stringify(filter),
      noTest: true
    };

    apiFacilityUser.callGet("", params).then(({status, data}) => {
      if (status === 200 && data) setUsers(data?.map(user => ({...user, id: user.publicId})) || []);
    });
  };

  // Microsoft Integration
  const getFolder = useCallback(
    async folderId =>
      apiMS
        .callGet(null, {
          resourceType: "sharepoint",
          resourcePath: "drive",
          resourceId: folderId
        })
        .then(({status, data}) => {
          if (status === 200 && data?.drive) {
            setTargetFolder(data.drive[0].parentReference);
            setValue("sharepointFolderId", folderId);
          }
        }),
    [apiMS, setValue]
  );

  // Initial Load
  useEffect(() => {
    if (isMounted() && reportMeta.sharepointFolderId) {
      if (!root?.current) {
        apiMS
          .callGet(null, {
            resourceType: "sharepoint",
            resourcePath: "drive"
          })
          .then(({status, data}) => {
            if (status === 200) {
              let targetParentFolder = null;

              const targetFolders = data.drive
                ?.filter(type => type?.folder)
                ?.map(folder => {
                  targetParentFolder = folder.parentReference;
                  return folder;
                });

              if (!targetParentFolder) targetParentFolder = {...data.root, id: "root"};

              setTargetFolderHasChildren(targetFolders?.length > 0);

              root.current = targetParentFolder;

              if (reportMeta.sharepointFolderId) getFolder(reportMeta.sharepointFolderId);
              else if (targetParentFolder) {
                setTargetFolder(targetParentFolder);
                setValue("sharepointFolderId", targetParentFolder.id);
              }
            }
          });
      }
    }
  }, [isMounted, reportMeta, getFolder, apiMS, setValue]);

  if (editTargetFolder && root?.current)
    return (
      <ModalCustomTableTarget
        visible={editTargetFolder}
        setVisible={setEditTargetFolder}
        target={root.current}
        setTarget={t => {
          setTargetFolder(t || root.current);
          setValue("sharepointFolderId", t?.id || root.current.id);
        }}
        root={root.current}
        goBack={() => setEditTargetFolder(false)}
        hasBackButton
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <HeadingCenter>Edit Report Metadata</HeadingCenter>

      <FormProvider {...form}>
        {!loading ? (
          <Form onSubmit={handleSubmit(save)} noValidate>
            <FormField>
              <InputText name="name" placeholder="Name" label="Name" required />
            </FormField>

            <SubMenu>
              <Label htmlFor="interval" bold>
                DATA FROM LAST
              </Label>
              <FormGroup inline>
                <FormField inline>
                  <InputNumber name="interval" placeholder="Interval..." />
                </FormField>
                <FormField inline>
                  <InputSelect
                    name="base"
                    placeholder="Base..."
                    options={[
                      {label: `Day${watch("interval") > 1 ? "s" : ""}`, name: "days"},
                      {label: `Week${watch("interval") > 1 ? "s" : ""}`, name: "weeks"},
                      {label: `Month${watch("interval") > 1 ? "s" : ""}`, name: "months"},
                      {label: `Year${watch("interval") > 1 ? "s" : ""}`, name: "years"}
                    ]}
                  />
                </FormField>
              </FormGroup>
            </SubMenu>

            {!reportMeta.sharepointFolderId && (
              <FormField>
                <InputCheck name="hasHeader">Include a page header?</InputCheck>
              </FormField>
            )}

            {hasHeader && (
              <SubMenu>
                <FormField>
                  <InputText name="header.name" label="Report Header" />
                </FormField>

                <FormField>
                  <InputCheck name="header.address">Include an address?</InputCheck>
                </FormField>

                <FormField>
                  <InputText name="header.topLeft" label="Top Left" maxLength={100} />
                </FormField>

                <FormField>
                  <InputText name="header.bottomLeft" label="Bottom Left" maxLength={100} />
                </FormField>

                <FormField>
                  <InputText name="header.topRight" label="Top Right" maxLength={100} />
                </FormField>

                <FormField>
                  <Label htmlFor="header.bottomRight" bold>
                    BOTTOM RIGHT
                  </Label>
                  <Warning>
                    Warning! This is a generated date, custom values will replace date.
                  </Warning>
                  <InputText name="header.bottomRight" maxLength={100} />
                </FormField>
              </SubMenu>
            )}

            <FormField>
              <InputCheck name="isAutomated">Automate report generation?</InputCheck>
            </FormField>

            {isAutomated && (
              <SubMenu>
                <FormField>
                  <InputRadioGroup
                    label="Based on"
                    name="automationType"
                    options={[
                      {label: "Frequency", value: "frequency"},
                      {label: "Task Completion", value: "completion"}
                    ]}
                  />
                </FormField>
                {automationType === "frequency" && (
                  <>
                    <FormField>
                      <InputSelect
                        name="frequency"
                        label="Generate"
                        placeholder="Frequency..."
                        options={
                          frequencies?.map(({label, name: freqName}) => ({
                            label: label,
                            name: freqName
                          })) || []
                        }
                      />
                    </FormField>
                    <FormGroup inline>
                      <FormField inline>
                        <InputDate name="startDate" label="Starting" />
                      </FormField>
                      <FormField inline>
                        <InputTime name="startTime" label="At" />
                      </FormField>
                    </FormGroup>
                  </>
                )}
                {automationType === "completion" && (
                  <>
                    <FormField>
                      <InputSelect
                        name="completionTaskType"
                        options={[
                          {label: "Checksheet", value: "checksheet"},
                          {label: "Event", value: "event"}
                        ]}
                        placeholder="Select task type..."
                        label="TASK TYPE"
                      />
                    </FormField>
                    {completionTaskType && completionOptions && (
                      <FormField>
                        <InputSelect
                          name="completionTaskId"
                          options={completionOptions}
                          placeholder={
                            completionTaskType === "checksheet"
                              ? "Select checksheet..."
                              : "Select event..."
                          }
                          label={
                            completionTaskType === "checksheet"
                              ? "SELECT CHECKSHEET"
                              : "SELECT EVENT"
                          }
                        />
                      </FormField>
                    )}
                    <SearchWrapper>
                      <SearchSelect
                        label="RECIPIENTS *"
                        placeholder="Send to users..."
                        search={handleSearchUser}
                        results={users}
                        setResults={setUsers}
                        add={selected => {
                          if (!completionTaskRecipients)
                            setValue("completionTaskRecipients", [selected.email]);
                          else if (
                            !completionTaskRecipients.map(email => email).includes(selected.email)
                          )
                            setValue("completionTaskRecipients", [
                              ...completionTaskRecipients,
                              selected.email
                            ]);
                        }}
                        showAll
                      />
                    </SearchWrapper>
                    <Recipients fullWidth>
                      {completionTaskRecipients?.map(email => (
                        <Button
                          quiet
                          key={`email-${email}`}
                          onClick={() =>
                            setValue(
                              "completionTaskRecipients",
                              completionTaskRecipients.filter(curr => {
                                if (curr.email !== email) return true;
                                return false;
                              })
                            )
                          }>
                          {email}&nbsp;
                          <FontAwesomeIcon icon={faClose} />
                        </Button>
                      ))}
                    </Recipients>
                    <InputError
                      atBottom={false}
                      errors={
                        completionTaskRecipients && completionTaskRecipients.length ? {} : errors
                      }
                      name="completionTaskRecipients"
                    />
                  </>
                )}
              </SubMenu>
            )}

            {reportMeta.sharepointFolderId && (
              <FormField>
                <Label htmlFor="sharepointFolderId" bold>
                  TARGET FOLDER
                </Label>
                <TargetFolder>
                  <input name="sharepointFolderId" hidden />
                  <Inline>
                    {targetFolder?.name ? (
                      <Text bold>
                        <Abbr title={targetFolder.id}>{targetFolder.name}</Abbr>
                      </Text>
                    ) : (
                      <>
                        <Text>Target...</Text>
                        <ButtonLoader />
                      </>
                    )}
                    {targetFolderHasChildren && (
                      <LabelButton type="button" onClick={() => setEditTargetFolder(true)}>
                        <Abbr title="Change target folder">
                          <FontAwesomeIcon icon={faEdit} />
                        </Abbr>
                      </LabelButton>
                    )}
                  </Inline>
                  <InputError name="sharepointFolderId" errors={errors} />
                </TargetFolder>
              </FormField>
            )}

            <Button type="submit" data-testid="checksheet.saveMeta">
              Save
            </Button>
          </Form>
        ) : (
          <NotLoaded>
            <Loader />
          </NotLoaded>
        )}
      </FormProvider>
    </Modal>
  );
};

ModalEditMeta.propTypes = {
  visible: PropTypes.bool,
  setVisible: PropTypes.func,
  reportMeta: PropTypes.objectOf(PropTypes.any),
  save: PropTypes.func
};

ModalEditMeta.defaultProps = {
  reportMeta: {},
  visible: false,
  setVisible: () => {},
  save: () => {}
};

// Style Overrides

const SubMenu = styled(FormGroup)`
  position: relative;
  padding: ${pad}px;
  margin-bottom: ${pad * 2}px;
  width: 100%;
  border: ${border} solid ${props => props.theme.secondary};
  border-radius: ${radius};
`;

// const InlineWrapper = styled(FormField)`
//   width: 100%;
//   gap: ${pad}px;
//   align-self: flex-start;

//   :nth-child(n) {
//     max-width: max-content;
//   }
// `;

// const StyledSpan = styled.span`
//   margin-top: ${pad}px;
// `;

const TargetFolder = styled.div`
  position: relative;
  width: fit-content;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: 0 ${pad}px;
`;

const LabelButton = styled.button`
  position: relative;
  display: block;
  width: fit-content;
  padding-left: ${pad}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: ${radius};

  ${({active}) =>
    active &&
    css`
      text-decoration: underline;
    `}
`;

const SearchWrapper = styled.div`
  position: relative;
  margin: ${pad}px 0;
`;

const Recipients = styled(Inline)`
  flex-wrap: wrap;
`;

export default ModalEditMeta;
